import type { ArticleBlock, Block, DesignElement } from '@/types/blocks'
import { sampleArticle } from './articleSample'
import type { ActiveBlockCard } from '../constants/blockTypeMatrix'
import { articleBlockBody, defaultBlockBody } from './blockDefaults'
import { createDesignElement } from './designElementDefaults'

/**
 * Примеры наполнения для окна «Новый экран»: автор видит, как будет выглядеть
 * экран, а не пустую заготовку. ТОЛЬКО для показа — в урок уходит пустое тело
 * по умолчанию (`defaultBlockBody`), примеры никогда не сохраняются.
 */
const sampleUrl = (name: string) => `${window.location.origin}/samples/${name}.svg`
const sampleImage = () => sampleUrl('info-sample')

const answers = (items: Array<[string, boolean]>) =>
  items.map(([text, isCorrect]) => ({ text, isCorrect }))

// Экран из курса «Китайский чай» (блок 4446) — настоящий пример автора.
const TEA_IMAGE = 'https://s3-academy.crypton.xyz/media/251b41de-5de2-42a7-8a35-46286718be6f.webp'

const designElements = (): DesignElement[] => {
  const heading = createDesignElement('heading')
  const text = createDesignElement('text')
  const image = createDesignElement('image')
  return [
    {
      ...heading,
      backdrop: 'blur',
      content: 'Один куст, шесть чаёв',
      contentEn: 'One Bush, Six Teas',
    },
    {
      ...text,
      backdrop: 'dark',
      content:
        'Зелёный, белый, жёлтый, улун, красный, тёмный — это не шесть растений. Лист у них может быть с одного куста.\n\nРазличает их <mark>ключевая операция</mark>: что с листом сделали в первые часы после сбора.',
      contentEn:
        'Green, white, yellow, oolong, red, dark — these are not six different plants. Their leaves can come from a single bush.\n\nWhat distinguishes them is a <mark>key operation</mark>: what was done with the leaf in the first hours after harvesting.',
    },
    { ...image, imageUrl: TEA_IMAGE, imageUrlEn: TEA_IMAGE },
  ]
}

const sampleBody = (card: ActiveBlockCard): Partial<Block> => {
  switch (card.id) {
    case 'image':
      return { image: sampleImage(), imageEn: sampleImage() } as Partial<Block>
    case 'video':
      return { videoId: 'sample', videoIdEn: 'sample' } as Partial<Block>
    case 'article':
      return { article: sampleArticle(), available: true } as Partial<ArticleBlock>
    case 'design':
      return { elements: designElements() } as Partial<Block>
    case 'quiz-single':
    case 'quiz-multi': {
      const multi = card.id === 'quiz-multi'
      const body = {
        question: multi ? 'Что из этого — фрукты?' : 'Какая планета ближе всего к Солнцу?',
        answers: multi
          ? answers([
              ['Яблоко', true],
              ['Банан', true],
              ['Морковь', false],
              ['Груша', true],
            ])
          : answers([
              ['Меркурий', true],
              ['Венера', false],
              ['Марс', false],
            ]),
      }
      return { body, bodyEn: body } as Partial<Block>
    }
    case 'yes_no': {
      const body = { question: 'Кит — это рыба?', answers: [] }
      return { body, bodyEn: body, correctAnswer: false } as Partial<Block>
    }
    case 'fill_blank': {
      const body = {
        question: 'Столица Франции — ___.',
        answers: answers([
          ['Париж', true],
          ['Лондон', false],
          ['Рим', false],
        ]),
      }
      return { body, bodyEn: body } as Partial<Block>
    }
    case 'match': {
      const pair = (l: string, r: string, i: number) => ({
        leftText: l,
        leftTextEn: l,
        rightText: r,
        rightTextEn: r,
        leftOrder: i,
        rightOrder: 2 - i,
      })
      return {
        pairs: [
          pair('Фотосинтез', 'Растение делает энергию из света', 0),
          pair('Гравитация', 'Сила, которая притягивает тела', 1),
          pair('Испарение', 'Вода превращается в пар', 2),
        ],
      } as Partial<Block>
    }
  }
}

/** У видео-примера нет ролика в Kinescope: показываем зацикленную 5-секундную
 *  анимацию тем же полноэкранным слоем, каким превью рисует картинку. */
const videoSample = (): Block =>
  ({
    ...defaultBlockBody({ backendType: 'info' } as ActiveBlockCard),
    image: sampleUrl('video-sample'),
    imageEn: sampleUrl('video-sample'),
    id: -1,
    order: 0,
    createdAt: '',
    updatedAt: '',
  }) as Block

export const blockSample = (card: ActiveBlockCard): Block => {
  if (card.id === 'video') return videoSample()
  const base = card.backendType === 'article' ? articleBlockBody(0) : defaultBlockBody(card)
  return { ...base, ...sampleBody(card), id: -1, order: 0, createdAt: '', updatedAt: '' } as Block
}
